import { call, put, takeLatest, select } from 'redux-saga/effects';
import * as http from 'utils/http';
import config from 'config';
import { replaceWithQuery as replace } from 'utils/routing';

import {
  setInitialRecurringOrder,
  setMainLoading,
  showPreviewModalFor,
  hidePreviewModal,
  setPreviewChanges,
  setPreviewModalSubmitting,
} from 'redux/modules/recurring-orders-edit/duck';

import {
  doneUpdateRecurringOrderInfo,
  requestUpdateRecurringOrderInfo,
  cancelUpdateRecurringOrderInfo,
} from 'redux/modules/recurring-orders/duck';

import {
  fetchRecurringOrderInfo,
  fetchRecurringOrderActiveOrders,
} from 'redux/modules/recurring-orders/effects';

import { getPreviewModalMode } from 'redux/modules/recurring-orders-edit/selectors';
import { setShipperAddresses, setShipperUsers } from 'redux/modules/shipper-order-form/duck';
import { fetchAddresses } from 'redux/modules/addresses/effects';
import { EDIT, CANCEL } from 'modules/recurring-orders/ChangesPreviewModal/constants';
import { setUserSettings } from 'redux/modules/shipper-order-form/utils';
import { transformShipperOrderToRecurringOrder, computeRecurringOrder } from './utils';

const initForm$ = 'recurring-orders-edit/initForm$';
const getRecurringOrdersEditionPreview$ = 'recurring-orders-edit/getRecurringOrdersEditionPreview$';
const getRecurringOrdersCancelationPreview$ =
  'recurring-orders-edit/getRecurringOrdersCancelationPreview$';
const closePreviewModal$ = 'recurring-orders-edit/closePreviewModal$';
const editRecurringOrder$ = 'recurring-orders-edit/editRecurringOrder$';

export const initForm = (id) => ({
  type: initForm$,
  payload: {
    id,
  },
});

export const getRecurringOrdersEditionPreview = (recurringOrder) => ({
  type: getRecurringOrdersEditionPreview$,
  payload: {
    recurringOrder,
  },
});

export const getRecurringOrdersCancelationPreview = (recurringOrder) => ({
  type: getRecurringOrdersCancelationPreview$,
  payload: {
    recurringOrder,
  },
});

export const closePreviewModal = () => ({
  type: closePreviewModal$,
});

export const editRecurringOrder = (recurringOrder) => ({
  type: editRecurringOrder$,
  payload: {
    recurringOrder,
  },
});

function* handleEditFormInit({ payload }) {
  const { id } = payload;

  if (!id) {
    return;
  }

  try {
    yield put(setMainLoading(true));

    const recurringOrder = yield call(http.get, `${config.API_URL}mercury/recurring_orders/${id}/`);
    yield put(setInitialRecurringOrder(computeRecurringOrder(recurringOrder)));

    const { shipper, user } = recurringOrder;

    yield put(setShipperUsers(shipper?.users));

    const addresses = yield call(fetchAddresses, shipper);
    yield put(setShipperAddresses(addresses));

    yield call(setUserSettings, user);
  } catch (err) {
    yield call(http.getError, err);
  } finally {
    yield put(setMainLoading(false));
  }
}

function* handleEditPreviewChanges({ payload }) {
  const { recurringOrder } = payload;

  if (!recurringOrder) {
    return;
  }

  const recurringOrderBody = yield call(transformShipperOrderToRecurringOrder, recurringOrder);

  try {
    yield put(showPreviewModalFor(EDIT));
    const changes = yield call(
      http.patch,
      `${config.API_URL}mercury/recurring_orders/${recurringOrder.id}/preview/`,
      recurringOrderBody
    );
    yield put(setPreviewChanges(changes));
  } catch (error) {
    yield call(http.getError, error);
  }
}

function* handleCancelPreviewChanges({ payload }) {
  const { recurringOrder } = payload;

  if (!recurringOrder) {
    return;
  }

  const { user, shipper } = recurringOrder;
  const recurringOrderBody = { user, shipper };

  try {
    yield put(showPreviewModalFor(CANCEL));
    const changes = yield call(
      http.patch,
      `${config.API_URL}mercury/recurring_orders/${recurringOrder.id}/preview_cancel/`,
      recurringOrderBody
    );
    yield put(setPreviewChanges(changes));
  } catch (error) {
    yield call(http.getError, error);
  }
}

function* handleClosePreviewModal() {
  yield put(hidePreviewModal());
}

function* handleEditRecurringOrder({ payload }) {
  const { recurringOrder } = payload;

  if (!recurringOrder) {
    return;
  }

  const mode = yield select(getPreviewModalMode);

  try {
    const recurringOrderBody =
      mode === EDIT
        ? transformShipperOrderToRecurringOrder(recurringOrder)
        : { rule_status: 'CANCELLED' };
    yield put(setPreviewModalSubmitting(true));
    yield call(
      http.patch,
      `${config.API_URL}mercury/recurring_orders/${recurringOrder.id}/`,
      recurringOrderBody
    );
    yield put(setPreviewModalSubmitting(false));

    yield put(hidePreviewModal());
    yield put(requestUpdateRecurringOrderInfo());
    yield call(replace, { pathname: `/recurring-orders/${recurringOrder.id}/` });
    const recurringOrderInfo = yield call(fetchRecurringOrderInfo, recurringOrder.id);
    const recurringOrderActiveOrders = yield call(
      fetchRecurringOrderActiveOrders,
      recurringOrder.id
    );
    yield put(
      doneUpdateRecurringOrderInfo({
        activeOrders: recurringOrderActiveOrders,
        recurringOrder: recurringOrderInfo,
      })
    );
  } catch (error) {
    yield call(http.getError, error);
    yield put(cancelUpdateRecurringOrderInfo(error));
  }
}

export function* watchRecurringOrdersEdit() {
  yield takeLatest(initForm$, handleEditFormInit);
  yield takeLatest(getRecurringOrdersEditionPreview$, handleEditPreviewChanges);
  yield takeLatest(getRecurringOrdersCancelationPreview$, handleCancelPreviewChanges);
  yield takeLatest(closePreviewModal$, handleClosePreviewModal);
  yield takeLatest(editRecurringOrder$, handleEditRecurringOrder);
}
